import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Paper, Typography, Button, TextField, MenuItem, Table, TableHead,
  TableRow, TableCell, TableBody, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, Chip, Tooltip, Stack, InputAdornment,
  alpha, Divider, Avatar,
  ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import AddIcon              from '@mui/icons-material/Add';
import EditIcon             from '@mui/icons-material/Edit';
import DeleteIcon           from '@mui/icons-material/DeleteOutline';
import SearchIcon           from '@mui/icons-material/Search';
import MenuBookIcon         from '@mui/icons-material/MenuBook';
import GavelIcon            from '@mui/icons-material/Gavel';
import PolicyIcon           from '@mui/icons-material/Policy';
import VisibilityIcon       from '@mui/icons-material/Visibility';
import BusinessIcon         from '@mui/icons-material/Business';
import GridViewIcon         from '@mui/icons-material/GridView';
import TableRowsIcon        from '@mui/icons-material/TableRows';
import CloseIcon            from '@mui/icons-material/Close';
import apiService from '../services/apiServices';
import { getAuthToken } from '../services/authSession';
import useConfirm from '../hooks/useConfirm';

const API = process.env.REACT_APP_API_URL || '';
const auth = getAuthToken;

const CATS  = ['Rregullore', 'Politika', 'HR', 'Disiplinore', 'IT', 'Financiare', 'Punë', 'Siguri', 'Tjera'];
// Rregulloret dhe Politikat ndajnë të njëjtin depo (`companyPolicies`), por
// shfaqen si dy module: njëri mban vetëm kategorinë "Rregullore", tjetri gjithë
// të tjerat. Konstantet rrinë jashtë komponentit që referenca të mos ndryshojë
// në çdo render (përndryshe useMemo-t rillogariten pa nevojë).
const REG_CAT      = 'Rregullore';
const REG_EXCLUDE  = [REG_CAT];
const POLICY_CATS  = CATS.filter(c => c !== REG_CAT);
const EMPTY = { title: '', content: '', category: 'HR', department: '', status: 'active', version: '1.0' };

const CAT_COLORS = {
  'Rregullore':  { bg: '#FEF2F2', color: '#B91C1C' },
  'Politika':    { bg: '#FDF4FF', color: '#7E22CE' },
  'HR':          { bg: '#EFF6FF', color: '#1D4ED8' },
  'Disiplinore': { bg: '#FFF7ED', color: '#C2410C' },
  'IT':          { bg: '#F0FDF4', color: '#15803D' },
  'Financiare':  { bg: '#FEFCE8', color: '#A16207' },
  'Punë':        { bg: '#FDF4FF', color: '#7E22CE' },
  'Siguri':      { bg: '#FFF1F2', color: '#BE123C' },
  'Operacional': { bg: '#ECFEFF', color: '#0E7490' },
  'Cilësi':      { bg: '#F0FDF4', color: '#166534' },
  'Tjera':       { bg: '#F8FAFC', color: '#475569' },
};

function CatChip({ label }) {
  const c = CAT_COLORS[label] || { bg: '#F1F5F9', color: '#334155' };
  return (
    <Chip
      size="small"
      label={label}
      sx={{
        bgcolor: c.bg, color: c.color,
        fontWeight: 700, fontSize: '0.7rem',
        border: `1px solid ${alpha(c.color, 0.18)}`,
        height: 22,
      }}
    />
  );
}

function StatusBadge({ status }) {
  const active = status === 'active';
  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <Box sx={{
        width: 7, height: 7, borderRadius: '50%',
        bgcolor: active ? '#22c55e' : '#94a3b8',
        boxShadow: active ? '0 0 0 2px #bbf7d0' : 'none',
      }} />
      <Typography variant="caption" sx={{ color: active ? '#15803d' : '#64748b', fontWeight: 600, fontSize: '0.72rem' }}>
        {active ? 'Aktiv' : 'Joaktiv'}
      </Typography>
    </Stack>
  );
}

/** Metrikë inline — e njëjta gjuhë vizuale si te Qendra e Njohurive. */
function Metric({ value, label, color }) {
  return (
    <Stack direction="row" alignItems="baseline" spacing={0.6} sx={{ whiteSpace: 'nowrap' }}>
      <Typography sx={{ fontWeight: 800, fontSize: '0.875rem', lineHeight: 1, color: color || 'text.primary' }}>{value}</Typography>
      <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 500 }}>{label}</Typography>
    </Stack>
  );
}

function DocTable({ store, label, cats, docLabel, accentColor, Icon, onlyCat, excludeCats }) {
  const [rows,       setRows]       = useState([]);
  const [depts,      setDepts]      = useState([]);
  const [form,       setForm]       = useState(EMPTY);
  const [dlgOpen,    setDlgOpen]    = useState(false);
  const [editing,    setEditing]    = useState(null);
  const [viewRow,    setViewRow]    = useState(null);
  const [search,     setSearch]     = useState('');
  const [filterCat,  setFilterCat]  = useState('');
  const [filterDept, setFilterDept] = useState('');
  const [filterSt,   setFilterSt]   = useState('');
  const [viewMode,   setViewMode]   = useState('table');

  const load = useCallback(async () => {
    const [r, d] = await Promise.all([
      apiService().get(store),
      apiService().get('departments').catch(() => ({ data: [] })),
    ]);
    setRows(r?.data || []);
    setDepts(d?.data || []);
  }, [store]);

  useEffect(() => { load(); }, [load]);

  // Rreshtat që i takojnë këtij moduli — para çdo filtri të përdoruesit.
  const scoped = useMemo(() => {
    let out = rows;
    if (onlyCat)              out = out.filter(r => r.category === onlyCat);
    if (excludeCats?.length)  out = out.filter(r => !excludeCats.includes(r.category));
    return out;
  }, [rows, onlyCat, excludeCats]);

  // Real organization departments (+ any already used on rows), instead of a
  // hardcoded generic list — so each procedure shows/filters by its real dept.
  const deptOptions = useMemo(() => {
    const real     = depts.map(d => d.name || d.Name).filter(Boolean);
    const fromRows = scoped.map(r => r.department).filter(Boolean);
    return ['Të gjitha', ...Array.from(new Set([...real, ...fromRows]))];
  }, [depts, scoped]);

  const filtered = useMemo(() =>
    scoped.filter(r => {
      if (search    && !r.title.toLowerCase().includes(search.toLowerCase()) && !(r.category||'').toLowerCase().includes(search.toLowerCase())) return false;
      if (filterCat  && r.category !== filterCat) return false;
      if (filterDept && r.department !== filterDept) return false;
      if (filterSt   && r.status !== filterSt) return false;
      return true;
    })
  , [scoped, search, filterCat, filterDept, filterSt]);

  const stats = useMemo(() => ({
    total:    scoped.length,
    active:   scoped.filter(r => r.status === 'active').length,
    inactive: scoped.filter(r => r.status === 'inactive').length,
    recent:   scoped.filter(r => {
      // Backend-i i kthen fushat në camelCase (`createdAt`); rreshtat e vjetër
      // nga localStorage e kanë `created_at`. Pranoji të dyja, përndryshe
      // numëruesi mbetet përgjithmonë 0.
      const created = r.createdAt || r.created_at;
      if (!created) return false;
      return (Date.now() - new Date(created).getTime()) < 30 * 86400 * 1000;
    }).length,
  }), [scoped]);

  const openAdd  = () => { setForm({...EMPTY, category: onlyCat || cats[0]}); setEditing(null); setDlgOpen(true); };
  const openEdit = (r) => { setForm({...r}); setEditing(r.id); setDlgOpen(true); };

  const save = async () => {
    if (editing) await apiService().put(store, { ...form, id: editing });
    else         await apiService().post(store, form);
    setDlgOpen(false); load();
  };

  const [confirm, confirmUI] = useConfirm();


  const del = async (id) => {
    if (!(await confirm({ title: 'Fshi dokumentin', message: `Fshij këtë ${docLabel}?`, confirmLabel: 'Fshi' }))) return;
    await fetch(`${API}/api/${store}/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${auth()}` } });
    load();
  };

  const accent = accentColor || '#3b82f6';

  return (
    <Box>
      {/* Toolbar */}
      <Paper variant="outlined" sx={{ px: 1.5, py: 1, mb: 1.25, borderRadius: 2.5 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <TextField
            size="small"
            placeholder={`Kërko ${label.toLowerCase()}…`}
            value={search}
            onChange={e => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: 'text.disabled' }} /></InputAdornment> }}
            sx={{ width: 220 }}
          />
          {!onlyCat && (
            <TextField select size="small" label="Kategoria" value={filterCat}
              onChange={e => setFilterCat(e.target.value)} sx={{ width: 145 }}>
              <MenuItem value="">Të gjitha</MenuItem>
              {cats.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
          )}
          <TextField select size="small" label="Departamenti" value={filterDept}
            onChange={e => setFilterDept(e.target.value)} sx={{ width: 152 }}>
            {deptOptions.map(d => <MenuItem key={d} value={d === 'Të gjitha' ? '' : d}>{d}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Statusi" value={filterSt}
            onChange={e => setFilterSt(e.target.value)} sx={{ width: 120 }}>
            <MenuItem value="">Të gjitha</MenuItem>
            <MenuItem value="active">Aktiv</MenuItem>
            <MenuItem value="inactive">Joaktiv</MenuItem>
          </TextField>
          <Box flex={1} />
          <ToggleButtonGroup size="small" value={viewMode} exclusive onChange={(_, v) => v && setViewMode(v)}>
            <ToggleButton value="table" sx={{ px: 1.2 }}><Tooltip title="Tabelë"><TableRowsIcon sx={{ fontSize: 16 }} /></Tooltip></ToggleButton>
            <ToggleButton value="grid"  sx={{ px: 1.2 }}><Tooltip title="Kartat"><GridViewIcon  sx={{ fontSize: 16 }} /></Tooltip></ToggleButton>
          </ToggleButtonGroup>
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openAdd}
            sx={{ borderRadius: 2, fontWeight: 700, textTransform: 'none', boxShadow: 'none', whiteSpace: 'nowrap', bgcolor: accent, '&:hover': { bgcolor: alpha(accent, 0.85), boxShadow: 'none' } }}>
            Shto {docLabel}
          </Button>
        </Stack>
      </Paper>

      {/* Metrikat + numëruesi i rezultateve — një rresht i vetëm, pa kartela */}
      <Stack direction="row" alignItems="center" justifyContent="space-between"
        flexWrap="wrap" useFlexGap sx={{ mb: 0.75, px: 0.5, rowGap: 0.5 }}>
        <Stack direction="row" alignItems="center" spacing={1.25} flexWrap="wrap" useFlexGap
          divider={<Divider orientation="vertical" flexItem sx={{ my: 0.25 }} />}>
          <Metric value={stats.total}    label="gjithsej" />
          <Metric value={stats.active}   label="aktive"          color="#15803d" />
          <Metric value={stats.inactive} label="joaktive"        color="#64748b" />
          <Metric value={stats.recent}   label="të reja (30 ditë)" color="#b45309" />
        </Stack>
        <Typography variant="caption" color="text.disabled">
          {filtered.length} {filtered.length === 1 ? 'rezultat' : 'rezultate'}
          {(filterCat || filterDept || filterSt || search) && ' • filtruar'}
        </Typography>
      </Stack>

      {/* TABLE VIEW */}
      {viewMode === 'table' && (
        <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', py: 1.25 }}>Titulli</TableCell>
                {!onlyCat && <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Kategoria</TableCell>}
                <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Departamenti</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Versioni</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Statusi</TableCell>
                <TableCell width={90} />
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={onlyCat ? 5 : 6} align="center" sx={{ py: 7 }}>
                    <Stack alignItems="center" spacing={1}>
                      <Avatar sx={{ bgcolor: '#F1F5F9', width: 52, height: 52 }}>
                        <Icon sx={{ fontSize: 26, color: '#CBD5E1' }} />
                      </Avatar>
                      <Typography variant="body2" color="text.disabled" fontWeight={500}>Nuk ka {docLabel.toLowerCase()} për të shfaqur</Typography>
                      <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={openAdd}
                        sx={{ mt: 1, textTransform: 'none', borderRadius: 1.5, borderColor: alpha(accent, 0.4), color: accent }}>
                        Shto {docLabel} të parë
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              )}
              {filtered.map((r, i) => (
                <TableRow
                  key={r.id}
                  hover
                  onClick={() => setViewRow(r)}
                  sx={{
                    cursor: 'pointer',
                    borderTop: i > 0 ? '1px solid #F1F5F9' : 'none',
                    '&:hover': { bgcolor: alpha(accent, 0.03) },
                    transition: 'background 0.15s',
                  }}
                >
                  <TableCell sx={{ py: 1.1, width: '40%', maxWidth: 520 }}>
                    <Tooltip title={r.title} enterDelay={600}>
                      <Typography variant="body2" fontWeight={700} color="text.primary" noWrap>{r.title}</Typography>
                    </Tooltip>
                    {r.content && (
                      <Typography variant="caption" color="text.disabled" noWrap sx={{ display: 'block', mt: 0.15 }}>
                        {r.content.slice(0, 90)}{r.content.length > 90 ? '…' : ''}
                      </Typography>
                    )}
                  </TableCell>
                  {!onlyCat && <TableCell><CatChip label={r.category} /></TableCell>}
                  <TableCell>
                    {r.department
                      ? <Stack direction="row" alignItems="center" spacing={0.5}><BusinessIcon sx={{ fontSize: 13, color: '#94a3b8' }} /><Typography variant="caption" color="text.secondary">{r.department}</Typography></Stack>
                      : <Typography variant="caption" color="text.disabled">—</Typography>}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={`v${r.version}`}
                      sx={{ bgcolor: '#F8FAFC', color: '#475569', border: '1px solid #E2E8F0', fontWeight: 700, fontSize: '0.68rem', height: 20 }} />
                  </TableCell>
                  <TableCell><StatusBadge status={r.status} /></TableCell>
                  <TableCell align="right" sx={{ pr: 1.5 }}>
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Tooltip title="Shiko"><IconButton size="small" onClick={e => { e.stopPropagation(); setViewRow(r); }} sx={{ color: '#64748b', '&:hover': { bgcolor: alpha(accent, 0.08), color: accent } }}><VisibilityIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                      <Tooltip title="Edito"><IconButton size="small" onClick={e => { e.stopPropagation(); openEdit(r); }} sx={{ color: '#64748b', '&:hover': { bgcolor: '#EFF6FF', color: '#2563eb' } }}><EditIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                      <Tooltip title="Fshij"><IconButton size="small" onClick={e => { e.stopPropagation(); del(r.id); }} sx={{ color: '#64748b', '&:hover': { bgcolor: '#FFF1F2', color: '#e11d48' } }}><DeleteIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* GRID / CARD VIEW */}
      {viewMode === 'grid' && (
        <Box>
          {filtered.length === 0 && (
            <Paper variant="outlined" sx={{ py: 7, borderRadius: 2.5, textAlign: 'center' }}>
              <Stack alignItems="center" spacing={1}>
                <Avatar sx={{ bgcolor: '#F1F5F9', width: 52, height: 52 }}>
                  <Icon sx={{ fontSize: 26, color: '#CBD5E1' }} />
                </Avatar>
                <Typography variant="body2" color="text.disabled" fontWeight={500}>Nuk ka {docLabel.toLowerCase()} për të shfaqur</Typography>
                <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={openAdd}
                  sx={{ mt: 1, textTransform: 'none', borderRadius: 1.5, borderColor: alpha(accent, 0.4), color: accent }}>
                  Shto {docLabel} të parë
                </Button>
              </Stack>
            </Paper>
          )}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 2 }}>
            {filtered.map(r => (
              <Paper
                key={r.id}
                variant="outlined"
                sx={{
                  p: 2.5, borderRadius: 2.5, cursor: 'pointer', transition: 'all 0.18s',
                  '&:hover': { boxShadow: `0 4px 20px ${alpha(accent, 0.13)}`, borderColor: alpha(accent, 0.35), transform: 'translateY(-1px)' },
                }}
                onClick={() => setViewRow(r)}
              >
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between" mb={1}>
                  {onlyCat ? <Box /> : <CatChip label={r.category} />}
                  <StatusBadge status={r.status} />
                </Stack>
                <Typography variant="body1" fontWeight={700} color="text.primary" mb={0.5} sx={{ lineHeight: 1.35 }}>{r.title}</Typography>
                {r.content && (
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                    {r.content}
                  </Typography>
                )}
                <Divider sx={{ my: 1.2 }} />
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Stack direction="row" spacing={1} alignItems="center">
                    {r.department && (
                      <Stack direction="row" alignItems="center" spacing={0.4}>
                        <BusinessIcon sx={{ fontSize: 12, color: '#94a3b8' }} />
                        <Typography variant="caption" color="text.disabled">{r.department}</Typography>
                      </Stack>
                    )}
                    <Chip size="small" label={`v${r.version}`}
                      sx={{ bgcolor: '#F8FAFC', color: '#475569', border: '1px solid #E2E8F0', fontWeight: 700, fontSize: '0.65rem', height: 18 }} />
                  </Stack>
                  <Stack direction="row" spacing={0.3}>
                    <Tooltip title="Edito"><IconButton size="small" onClick={e => { e.stopPropagation(); openEdit(r); }} sx={{ '&:hover': { bgcolor: '#EFF6FF', color: '#2563eb' } }}><EditIcon sx={{ fontSize: 14 }} /></IconButton></Tooltip>
                    <Tooltip title="Fshij"><IconButton size="small" onClick={e => { e.stopPropagation(); del(r.id); }} sx={{ '&:hover': { bgcolor: '#FFF1F2', color: '#e11d48' } }}><DeleteIcon sx={{ fontSize: 14 }} /></IconButton></Tooltip>
                  </Stack>
                </Stack>
              </Paper>
            ))}
          </Box>
        </Box>
      )}

      {/* VIEW DIALOG */}
      <Dialog open={!!viewRow} onClose={() => setViewRow(null)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        {viewRow && (
          <>
            <DialogTitle sx={{ pb: 1, pr: 2 }}>
              <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                <Box>
                  <Typography variant="h6" fontWeight={700} lineHeight={1.2}>{viewRow.title}</Typography>
                  <Stack direction="row" spacing={1} mt={0.8} alignItems="center">
                    {!onlyCat && <CatChip label={viewRow.category} />}
                    <StatusBadge status={viewRow.status} />
                    <Chip size="small" label={`v${viewRow.version}`}
                      sx={{ bgcolor: '#F8FAFC', color: '#475569', border: '1px solid #E2E8F0', fontWeight: 700, fontSize: '0.68rem', height: 20 }} />
                    {viewRow.department && <Stack direction="row" alignItems="center" spacing={0.4}><BusinessIcon sx={{ fontSize: 12, color: '#94a3b8' }} /><Typography variant="caption" color="text.secondary">{viewRow.department}</Typography></Stack>}
                  </Stack>
                </Box>
                <IconButton size="small" onClick={() => setViewRow(null)} sx={{ mt: 0.5 }}><CloseIcon fontSize="small" /></IconButton>
              </Stack>
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ pt: 2 }}>
              {viewRow.content
                ? <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.75 }}>{viewRow.content}</Typography>
                : <Typography variant="body2" color="text.disabled" fontStyle="italic">Nuk ka përmbajtje të shtuar.</Typography>}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={() => { setViewRow(null); openEdit(viewRow); }} startIcon={<EditIcon />} sx={{ textTransform: 'none' }}>Edito</Button>
              <Button variant="contained" onClick={() => setViewRow(null)} sx={{ textTransform: 'none', borderRadius: 2, bgcolor: accent, boxShadow: 'none', '&:hover': { boxShadow: 'none', bgcolor: alpha(accent, 0.85) } }}>Mbyll</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* EDIT / ADD DIALOG */}
      <Dialog open={dlgOpen} onClose={() => setDlgOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ borderBottom: '1px solid #F1F5F9', pb: 1.5 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Avatar sx={{ bgcolor: alpha(accent, 0.1), color: accent, width: 36, height: 36, borderRadius: 1.5 }}>
              <Icon sx={{ fontSize: 18 }} />
            </Avatar>
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>{editing ? `Edito ${docLabel}` : `${docLabel} i Ri`}</Typography>
              <Typography variant="caption" color="text.secondary">{editing ? 'Ndrysho të dhënat e dokumentit' : 'Plotëso të dhënat e dokumentit të ri'}</Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5 }}>
          <Stack spacing={2.5} mt={0.5}>
            <TextField
              size="small" label="Titulli *" value={form.title}
              onChange={e => setForm(p => ({...p, title: e.target.value}))}
              placeholder="p.sh. Procedura e Onboardingut të Punonjësve"
              InputProps={{ sx: { borderRadius: 1.5 } }}
            />
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
              {!onlyCat && (
                <TextField select size="small" label="Kategoria" value={form.category}
                  onChange={e => setForm(p => ({...p, category: e.target.value}))} sx={{ flex: 1, minWidth: 130 }}
                  InputProps={{ sx: { borderRadius: 1.5 } }}>
                  {cats.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </TextField>
              )}
              <TextField select size="small" label="Departamenti" value={form.department}
                onChange={e => setForm(p => ({...p, department: e.target.value}))} sx={{ flex: 1, minWidth: 140 }}
                InputProps={{ sx: { borderRadius: 1.5 } }}>
                {deptOptions.map(d => <MenuItem key={d} value={d === 'Të gjitha' ? '' : d}>{d}</MenuItem>)}
              </TextField>
              <TextField size="small" label="Versioni" value={form.version}
                onChange={e => setForm(p => ({...p, version: e.target.value}))} sx={{ width: 100 }}
                InputProps={{ sx: { borderRadius: 1.5 } }} />
              <TextField select size="small" label="Statusi" value={form.status}
                onChange={e => setForm(p => ({...p, status: e.target.value}))} sx={{ width: 120 }}
                InputProps={{ sx: { borderRadius: 1.5 } }}>
                <MenuItem value="active">Aktiv</MenuItem>
                <MenuItem value="inactive">Joaktiv</MenuItem>
              </TextField>
            </Stack>
            <TextField
              size="small" label="Përmbajtja" multiline rows={10} value={form.content}
              onChange={e => setForm(p => ({...p, content: e.target.value}))}
              placeholder="Shkruaj ose ngjit tekstin e plotë të dokumentit këtu…"
              InputProps={{ sx: { borderRadius: 1.5, fontFamily: 'inherit', fontSize: '0.875rem', lineHeight: 1.7 } }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #F1F5F9', gap: 1 }}>
          <Button onClick={() => setDlgOpen(false)} sx={{ textTransform: 'none', borderRadius: 2 }}>Anulo</Button>
          <Button variant="contained" onClick={save} disabled={!form.title}
            sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 700, bgcolor: accent, boxShadow: 'none', '&:hover': { boxShadow: 'none', bgcolor: alpha(accent, 0.85) } }}>
            {editing ? 'Ruaj ndryshimet' : `Krijo ${docLabel}`}
          </Button>
        </DialogActions>
      </Dialog>
      {confirmUI}
    </Box>
  );
}

export function RegulationsPage() {
  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1.5} mb={2}>
        <Avatar sx={{ bgcolor: alpha('#4f46e5', 0.1), color: '#4f46e5', width: 36, height: 36, borderRadius: 1.5 }}><GavelIcon sx={{ fontSize: 20 }} /></Avatar>
        <Box>
          <Typography variant="h6" fontWeight={800} color="text.primary" lineHeight={1.2}>Rregulloret</Typography>
          <Typography variant="caption" color="text.secondary">Rregulloret e brendshme dhe aktet normative të kompanisë</Typography>
        </Box>
      </Stack>
      <DocTable store="companyPolicies" label="Rregulloret" cats={CATS} docLabel="Rregullore"
        accentColor="#4f46e5" Icon={GavelIcon} onlyCat={REG_CAT} />
    </Box>
  );
}

export function PoliciesPage() {
  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1.5} mb={2}>
        <Avatar sx={{ bgcolor: alpha('#7c3aed', 0.1), color: '#7c3aed', width: 36, height: 36, borderRadius: 1.5 }}><PolicyIcon sx={{ fontSize: 20 }} /></Avatar>
        <Box>
          <Typography variant="h6" fontWeight={800} color="text.primary" lineHeight={1.2}>Politikat</Typography>
          <Typography variant="caption" color="text.secondary">Menaxho politikat e kompanisë</Typography>
        </Box>
      </Stack>
      <DocTable store="companyPolicies" label="Politikat" cats={POLICY_CATS} docLabel="Politikë"
        accentColor="#7c3aed" Icon={PolicyIcon} excludeCats={REG_EXCLUDE} />
    </Box>
  );
}

export function ProceduresPage() {
  const PCATS = ['Operacional', 'HR', 'IT', 'Financiare', 'Siguri', 'Cilësi', 'Tjera'];
  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1.5} mb={2}>
        <Avatar sx={{ bgcolor: alpha('#0ea5e9', 0.1), color: '#0ea5e9', width: 36, height: 36, borderRadius: 1.5 }}><MenuBookIcon sx={{ fontSize: 20 }} /></Avatar>
        <Box>
          <Typography variant="h6" fontWeight={800} color="text.primary" lineHeight={1.2}>Procedurat</Typography>
          <Typography variant="caption" color="text.secondary">Menaxho procedurat standarde të organizatës</Typography>
        </Box>
      </Stack>
      <DocTable store="procedures" label="Procedurat" cats={PCATS} docLabel="Procedurë" accentColor="#0ea5e9" Icon={MenuBookIcon} />
    </Box>
  );
}
